/**
 * The witch's own data: notes, lists, bookmarks and profile. Everything is written to the
 * device first (local-first); the sync engine copies it to the server when enabled.
 */
import {
  DEFAULT_SETTINGS,
  normalizeSettings,
  softDelete,
  touch,
  updateSettings,
  uuidv7,
  visible,
  type Settings,
} from "@hexling/core";
import type { Bookmark, FamiliarInput, List, ListItem, Note } from "@hexling/schema";
import type { Collection, LocalStore } from "../storage/types";

export type Ref = { category: Note["refs"][number]["category"]; slug: string };

export interface Profile {
  updatedAt: string;
  settings: Settings;
  familiar: FamiliarInput | null;
}

export type NoteDraft = Pick<Note, "title" | "body" | "tags" | "refs" | "occurredOn">;

const PROFILE_KEY = "profile";
const sameRef = (a: Ref, b: Ref) => a.category === b.category && a.slug === b.slug;

export class UserRepo {
  private listeners = new Set<(what: Collection | "profile") => void>();

  constructor(
    private readonly store: LocalStore,
    private readonly now: () => Date = () => new Date(),
  ) {}

  /** Subscribe to local changes (used to refresh queries and schedule sync). */
  onChange(fn: (what: Collection | "profile") => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(what: Collection | "profile") {
    for (const fn of this.listeners) fn(what);
  }

  private stamp() {
    const iso = this.now().toISOString();
    return { id: uuidv7(this.now().getTime()), updatedAt: iso, deletedAt: null };
  }

  private async put<T extends { id: string }>(c: Collection, records: T[]) {
    await this.store.putMany(c, records);
    this.emit(c);
  }

  // ---- notes

  async notes(): Promise<Note[]> {
    const all = visible(await this.store.all<Note>("notes"));
    return all.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  async note(id: string): Promise<Note | undefined> {
    return (await this.notes()).find((n) => n.id === id);
  }

  async saveNote(draft: NoteDraft, id?: string): Promise<Note> {
    const existing = id ? await this.note(id) : undefined;
    const note: Note = existing
      ? touch({ ...existing, ...draft }, this.now())
      : { ...this.stamp(), ...draft };
    await this.put("notes", [note]);
    return note;
  }

  async deleteNote(id: string): Promise<void> {
    const note = await this.note(id);
    if (note) await this.put("notes", [softDelete(note, this.now())]);
  }

  async notesAbout(ref: Ref): Promise<Note[]> {
    return (await this.notes()).filter((n) => n.refs.some((r) => sameRef(r, ref)));
  }

  // ---- lists

  async lists(): Promise<List[]> {
    return visible(await this.store.all<List>("lists")).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }

  async saveList(input: Pick<List, "name" | "description" | "icon">, id?: string): Promise<List> {
    const existing = id ? (await this.lists()).find((l) => l.id === id) : undefined;
    const list: List = existing
      ? touch({ ...existing, ...input }, this.now())
      : { ...this.stamp(), ...input };
    await this.put("lists", [list]);
    return list;
  }

  async deleteList(id: string): Promise<void> {
    const list = (await this.lists()).find((l) => l.id === id);
    if (!list) return;
    const items = await this.listItems(id);
    await this.store.putMany(
      "listItems",
      items.map((i) => softDelete(i, this.now())),
    );
    await this.put("lists", [softDelete(list, this.now())]);
  }

  async listItems(listId?: string): Promise<ListItem[]> {
    const all = visible(await this.store.all<ListItem>("listItems"));
    return (listId ? all.filter((i) => i.listId === listId) : all).sort(
      (a, b) => a.position - b.position,
    );
  }

  /** Add an item to a list. Adding the same item twice is a no-op. */
  async addToList(listId: string, ref: Ref, note = ""): Promise<ListItem> {
    const items = await this.listItems(listId);
    const dupe = items.find((i) => sameRef(i.ref, ref));
    if (dupe) return dupe;
    const item: ListItem = { ...this.stamp(), listId, ref, note, position: items.length };
    await this.put("listItems", [item]);
    return item;
  }

  async removeListItem(id: string): Promise<void> {
    const item = (await this.listItems()).find((i) => i.id === id);
    if (item) await this.put("listItems", [softDelete(item, this.now())]);
  }

  // ---- bookmarks

  async bookmarks(): Promise<Bookmark[]> {
    return visible(await this.store.all<Bookmark>("bookmarks")).sort((a, b) =>
      b.updatedAt.localeCompare(a.updatedAt),
    );
  }

  async isBookmarked(ref: Ref): Promise<boolean> {
    return (await this.bookmarks()).some((b) => sameRef(b.ref, ref));
  }

  /** Toggle a bookmark; returns the new state. */
  async toggleBookmark(ref: Ref): Promise<boolean> {
    const existing = (await this.bookmarks()).find((b) => sameRef(b.ref, ref));
    if (existing) {
      await this.put("bookmarks", [softDelete(existing, this.now())]);
      return false;
    }
    await this.put("bookmarks", [{ ...this.stamp(), ref }]);
    return true;
  }

  // ---- profile (settings + familiar)

  async profile(): Promise<Profile> {
    const stored = await this.store.getMeta<Profile>(PROFILE_KEY);
    return stored
      ? { ...stored, settings: normalizeSettings(stored.settings) }
      : { updatedAt: new Date(0).toISOString(), settings: { ...DEFAULT_SETTINGS }, familiar: null };
  }

  async replaceProfile(profile: Profile): Promise<void> {
    await this.store.setMeta(PROFILE_KEY, profile);
    this.emit("profile");
  }

  async updateSettings(patch: Parameters<typeof updateSettings>[1]): Promise<Settings> {
    const p = await this.profile();
    const settings = updateSettings(p.settings, patch);
    await this.replaceProfile({ ...p, settings, updatedAt: this.now().toISOString() });
    return settings;
  }

  async setFamiliar(familiar: FamiliarInput | null): Promise<void> {
    const p = await this.profile();
    await this.replaceProfile({ ...p, familiar, updatedAt: this.now().toISOString() });
  }

  /** A stable id for this device (seeds the card of the day when signed out). */
  async deviceId(): Promise<string> {
    const existing = await this.store.getMeta<string>("deviceId");
    if (existing) return existing;
    const id = uuidv7();
    await this.store.setMeta("deviceId", id);
    return id;
  }
}
